import { Label } from '../FormFields/Label';
import SelectField from '../FormFields/SelectField';
import InputField from '../FormFields/InputField';
import { formatLicencePlate } from 'web/src/utils/formatLicensePlate';
import CheckboxComponent from '../CheckboxComponent';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import * as Yup from 'yup';
import { Cart } from '@medusajs/medusa';
import { useEffect, useState } from 'react';
import getShippingData from '../../utils/getShippingData';
import messages from '../../data/messages';
import { Checkbox } from '../FormFields/Checkbox';
import { AddressValues, useStore } from 'web/src/lib/context/store-context';

const licenseRegex =
  /^(([A-Z]{2}-[\d]{2}-[\d]{2})|([\d]{2}-[\d]{2}-[A-Z]{2})|([\d]{2}-[A-Z]{2}-[\d]{2})|([A-Z]{2}-[\d]{2}-[A-Z]{2})|([A-Z]{2}-[A-Z]{2}-[\d]{2})|([\d]{2}-[A-Z]{2}-[A-Z]{2})|([\d]{2}-[A-Z]{3}-[\d])|([\d]-[A-Z]{3}-[\d]{2})|([A-Z]{2}-[\d]{3}-[A-Z])|([A-Z]-[\d]{3}-[A-Z]{2})|([A-Z]{3}-[\d]{2}-[A-Z])|([A-Z]-[\d]{2}-[A-Z]{3})|([\d]-[A-Z]{2}-[\d]{3})|([\d]{3}-[A-Z]{2}-[\d]$)|([A-Z]{2}[\d]{2}[\d]{2})|([\d]{2}[\d]{2}[A-Z]{2})|([\d]{2}[A-Z]{2}[\d]{2})|([A-Z]{2}[\d]{2}[A-Z]{2})|([A-Z]{2}[A-Z]{2}[\d]{2})|([\d]{2}[A-Z]{2}[A-Z]{2})|([\d]{2}[A-Z]{3}[\d])|([\d][A-Z]{3}[\d]{2})|([A-Z]{2}[\d]{3}[A-Z])|([A-Z][\d]{3}[A-Z]{2})|([A-Z]{3}[\d]{2}[A-Z])|([A-Z][\d]{2}[A-Z]{3})|([\d][A-Z]{2}[\d]{3})|([\d]{3}[A-Z]{2}[\d]$))$/;
const dutchPostalcode = /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i;

export type CheckoutFormSchema = Yup.InferType<typeof checkoutValidationSchema>;
export const checkoutValidationSchema = Yup.object({
  shipping_address: Yup.object({
    first_name: Yup.string().required(messages.fieldRequired),
    last_name: Yup.string().required(messages.fieldRequired),
    address_1: Yup.string().required(messages.fieldRequired),
    address_2: Yup.string().required(messages.fieldRequired),
    country_code: Yup.string().required(messages.fieldRequired),
    addition: Yup.string().optional(),
    postal_code: Yup.string().required(messages.fieldRequired).matches(dutchPostalcode, messages.postcodeNotValid),
    city: Yup.string().required(messages.fieldRequired),
    phone: Yup.string()
      .required(messages.fieldRequired)
      .test('invalidPhone', messages.phoneNotValid, val => val.length == 10),
  }),
  salutation: Yup.object()
    .required(messages.fieldRequired)
    .shape({
      value: Yup.string().required(messages.fieldRequired),
      label: Yup.string().required(messages.fieldRequired),
    }),
  email: Yup.string().email(messages.emailAddressNotValid).required(messages.fieldRequired),
  verify_email: Yup.string()
    .email(messages.emailAddressNotValid)
    .oneOf([Yup.ref('email')], 'E-mails komen niet overeen.')
    .required(messages.emailDoesNotMatch),
  newCarOwner: Yup.boolean(),
  licence_plate: Yup.string().when('newCarOwner', ([newCarOwner], schema) =>
    newCarOwner ? schema : schema.required(messages.fieldRequired).matches(licenseRegex, 'Voer een geldig kenteken in.')
  ),

  dealer: Yup.object()
    .required(messages.fieldRequired)
    .shape({
      value: Yup.string().required(messages.fieldRequired),
      label: Yup.string().required(messages.fieldRequired),
    }),
  conditions: Yup.boolean()
    .required('De voorwaarden moeten geaccepteerd worden')
    .oneOf([true], 'De voorwaarden moeten geaccepteerd worden'),
  newsletter: Yup.boolean().oneOf([true, false]),
  model_code: Yup.string().optional(),
  type_code: Yup.string().optional(),
  vehicle_id: Yup.string().optional(),
  color_code: Yup.string().optional(),
});

const CheckoutForm = ({ cart }: { cart: Cart }) => {
  const [isNewCarOwner, setIsNewCarOwner] = useState<boolean>(false);
  const { dealers } = useStore();

  const {
    reset,
    setFocus,
    control,
    setValue,
    register,
    formState: { errors },
    watch,
  } = useFormContext();
  const shippingData = getShippingData(cart?.shipping_address) as AddressValues;
  const shippingPostalCode = watch('shipping_address.postal_code');
  const shippingHouseNo = watch('shipping_address.address_2');
  const shippingAddition = watch('shipping_address.addition');
  const [formError, setFormError] = useState<boolean>(false);

  useEffect(() => {
    const firstError = (Object.keys(errors) as Array<keyof typeof errors>).reduce<keyof typeof errors | null>(
      (field, a) => {
        const fieldKey = field as keyof typeof errors;
        return !!errors[fieldKey] ? fieldKey : a;
      },
      null
    );

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  useEffect(() => {
    if (cart && isNewCarOwner) {
      reset({
        salutation: cart?.context?.salutation,
        dealer: cart?.context?.dealer,
        shipping_address: {
          ...shippingData,
          address_2: shippingData.address_2?.split(' ')[0],
          addition: shippingData.address_2?.split(' ')[1],
        },
        email: cart?.email,
        verify_email: cart?.email,
        licence_plate: cart?.context?.license_plate,
        newsletter: cart?.context?.newsletter,
        model_code: cart?.metadata?.model_code,
        type_code: cart?.metadata?.type_code,
        vehicle_id: cart?.metadata?.vehicle_id,
        color_code: cart?.metadata?.color_code,
        newCarOwner: isNewCarOwner,
      });
    } else {
      reset({
        salutation: cart?.context?.salutation,
        dealer: cart?.context?.dealer,
        shipping_address: {
          ...shippingData,
          address_2: shippingData.address_2?.split(' ')[0],
          addition: shippingData.address_2?.split(' ')[1],
        },
        email: cart?.email,
        verify_email: cart?.email,
        licence_plate: cart?.context?.license_plate,
        newsletter: cart?.context?.newsletter,
      });
    }
    if (cart.metadata && !isNewCarOwner) {
      setIsNewCarOwner(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, isNewCarOwner]);

  let timeOutId: ReturnType<typeof setTimeout> | null = null;

  useEffect(() => {
    if (shippingPostalCode && shippingHouseNo) {
      setFormError(false);
      timeOutId = setTimeout(() => {
        fetchDutchPostalCode(shippingPostalCode, shippingHouseNo, shippingAddition, 'shipping');
      }, 500);
    }
    return () => {
      if (timeOutId) {
        clearTimeout(timeOutId);
      }
    };
  }, [shippingPostalCode, shippingHouseNo, shippingAddition]);

  const fetchDutchPostalCode = async (
    postalCode: string,
    houseNo: string,
    addition: string | undefined,
    type: string
  ) => {
    const fetchUrl = `/api/postcode?postalCode=${postalCode}&houseNo=${houseNo}&houseNoAddition=${addition || ''}`;

    const fetchAddressData = await fetch(fetchUrl, {
      method: 'GET',
    })
      .then(response => response.json())
      .then(data => {
        return data;
      });

    if (fetchAddressData && !fetchAddressData.exception) {
      setValue('shipping_address.address_1', fetchAddressData.street);
      setValue('shipping_address.city', fetchAddressData.city);
    } else {
      setValue('shipping_address.address_1', '');
      setValue('shipping_address.city', '');
      setFormError(true);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col justify-between md:flex-row md:items-center">
        <InputField {...register('shipping_address.country_code')} value={'nl'} type="text" className={'hidden'} />
        <Controller
          control={control}
          name={'newCarOwner'}
          render={({ field: { onChange, value } }) => (
            <Checkbox className={'hidden'} id={'newCarOwner'} checked={value} onCheckedChange={onChange} />
          )}
        />

        <Label className="md:flex-1" htmlFor="salutation">
          Aanhef
        </Label>
        <div className="md:flex-1">
          <SelectField
            isSearchable={false}
            {...register('salutation')}
            options={[
              {
                label: 'De heer',
                value: 'heer',
              },
              {
                label: 'Mevrouw',
                value: 'mevrouw',
              },
            ]}
            name="salutation"
            placeholder="Selecteer"
          />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="shipping_address.first_name">
          Voornaam
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.first_name')} placeholder="Jan" type="text" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="shipping_address.last_name">
          Achternaam
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.last_name')} placeholder="Janssen" type="text" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="shipping_address.phone">
          Telefoonnummer
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.phone')} type="tel" placeholder="0612345678" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center ">
        <Label className="md:flex-1" htmlFor="email">
          E-mailadres
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('email')} placeholder="jan@janssen.nl" type="email" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center ">
        <Label className="md:flex-1" htmlFor="verify_email">
          Verifieer e-mailadres
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('verify_email')} placeholder="jan@janssen.nl" type="email" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="shipping_address.postal_code">
          Postcode
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.postal_code')} placeholder="1234AB" type="text" maxLength={6} />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="shipping_address.address_2">
          Huisnummer
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.address_2')} placeholder="123" type="text" maxLength={6} />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="shipping_address.addition">
          Toevoeging
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.addition')} placeholder="A" type="text" maxLength={6} />
        </div>
      </div>
      {formError && <p className="text-rg mt-2 error pt-1 text-red-600">Combinatie niet gevonden</p>}
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="ashipping_address.ddress_1">
          Straat
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.address_1')} placeholder="Mitsubishistraat" type="text" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center ">
        <Label className="md:flex-1" htmlFor="shipping_address.city">
          Plaats
        </Label>
        <div className="w-full md:flex-1">
          <InputField {...register('shipping_address.city')} placeholder="Utrecht" type="text" />
        </div>
      </div>
      <div className="flex flex-col items-start justify-between md:flex-row md:items-center ">
        <div className="md:flex-1">
          <p className="font-medium">Land/Regio</p>
        </div>
        <div className="my-2 md:my-0 md:flex-1">
          <p className="font-medium">Nederland</p>
        </div>
      </div>
      <h2 className="my-2 text-xl font-medium">Extra Informatie</h2>
      {!isNewCarOwner && (
        <div className="flex flex-col items-start justify-between md:flex-row md:items-center ">
          <Label className="md:flex-1" htmlFor="licence_plate">
            Kenteken
          </Label>
          <div className="w-full md:flex-1">
            <InputField
              {...register('licence_plate', {
                onChange: e => setValue('licence_plate', e && formatLicencePlate(e.target.value).toUpperCase()),
              })}
              placeholder="Kenteken met streepjes"
              type="text"
              maxLength={8}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col justify-between md:flex-row md:items-center">
        <Label className="md:flex-1" htmlFor="dealer">
          Selecteer uw dealer
        </Label>
        {dealers && (
          <div className="md:flex-1">
            <SelectField options={dealers} placeholder="Selecteer uw dealer" name="dealer" />
          </div>
        )}
      </div>
      {isNewCarOwner && (
        <>
          <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
            <Label className="md:flex-1" htmlFor="model_code">
              Model code
            </Label>
            <div className="w-full md:flex-1">
              <InputField
                {...register('model_code')}
                type="text"
                readOnly
                className="hover:cursor-not-allowed bg-brand-grey050"
              />
            </div>
          </div>
          <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
            <Label className="md:flex-1" htmlFor="type_code">
              Type code
            </Label>
            <div className="w-full md:flex-1">
              <InputField
                {...register('type_code')}
                type="text"
                readOnly
                className="hover:cursor-not-allowed bg-brand-grey050"
              />
            </div>
          </div>
          <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
            <Label className="md:flex-1" htmlFor="vehicle_id">
              Vehicle id
            </Label>
            <div className="w-full md:flex-1">
              <InputField
                {...register('vehicle_id')}
                type="text"
                readOnly
                className="hover:cursor-not-allowed bg-brand-grey050"
              />
            </div>
          </div>
          <div className="flex flex-col items-start justify-between md:flex-row md:items-center">
            <Label className="md:flex-1" htmlFor="color_code">
              Color code
            </Label>
            <div className="w-full md:flex-1">
              <InputField
                {...register('color_code')}
                type="text"
                readOnly
                className="hover:cursor-not-allowed bg-brand-grey050"
              />
            </div>
          </div>
        </>
      )}
      <div>
        <CheckboxComponent
          title="Ik ga akkoord met de voorwaarden"
          name="conditions"
          htmlDescription={
            'Jouw gegevens worden vastgelegd door Mitsubishi Motor Sales Nederland B.V. conform de Wet bescherming persoonsgegevens. Klik op deze knop om te bevestigen dat je kennis hebt genomen van ons <a class="font-medium underline" target="_blank" href="https://www.mitsubishi-motors.nl/legal/privacy-en-cookiebeleid">Privacy- en Cookiebeleid</a>.'
          }
        />
      </div>
      <div>
        <CheckboxComponent
          title="Schrijf u in voor onze nieuwsbrief (optioneel)"
          name="newsletter"
          htmlDescription={
            'Uw gegevens worden vastgelegd door Mitsubishi Motor Sales Nederland B.V. conform de Wet bescherming persoonsgegevens. We kunnen deze gebruiken om u te informeren over onze activiteiten, projecten en producten. Lees voor meer informatie ons <a class="font-medium underline" target="_blank" href="https://www.mitsubishi-motors.nl/legal/privacy-en-cookiebeleid">Privacy- en Cookiebeleid</a>. Indien u hier geen prijs op stelt, kunt u contact met ons opnemen op het volgende adres: Mitsubishi Motor Sales Nederland B.V., Postbus 9090, 1180 MB, Amstelveen, of via e-mail: contact@mmsn.nl.'
          }
        />
      </div>
    </div>
  );
};

export default CheckoutForm;
